import React from 'react';
import { Box, Typography } from '@mui/material';
import { SPECIES_CATALOG, SPREAD_CLASS_COUNTS, SpreadClassId } from './data/catalogData';

export interface SpreadClassItem {
  id: SpreadClassId;
  name: string;
  count: number;
  description: string;
  color: string;
}

export const SPREAD_CLASSES: SpreadClassItem[] = [
  {
    id: 'ubiquitous',
    name: 'Ubiquitous',
    count: SPREAD_CLASS_COUNTS.ubiquitous,
    description: '60% or more of deployments',
    color: '#10B98A',
  },
  {
    id: 'common',
    name: 'Common',
    count: SPREAD_CLASS_COUNTS.common,
    description: '30–59% of deployments',
    color: '#22C9E8',
  },
  {
    id: 'occasional',
    name: 'Occasional',
    count: SPREAD_CLASS_COUNTS.occasional,
    description: '10–29% of deployments',
    color: '#8B5CF6',
  },
  {
    id: 'rare',
    name: 'Rare',
    count: SPREAD_CLASS_COUNTS.rare,
    description: 'Under 10% of deployments',
    color: '#F59E0B',
  },
];

const TOTAL_SPECIES = SPECIES_CATALOG.length;
const CIRCLE_RADIUS = 54;
const CIRCUMFERENCE = 2 * Math.PI * CIRCLE_RADIUS; // ~339.29

interface SpeciesSpreadClassCardProps {
  selectedSpreadClass?: string | null;
  onSelectSpreadClass?: (id: string | null) => void;
}

const SpeciesSpreadClassCard: React.FC<SpeciesSpreadClassCardProps> = ({
  selectedSpreadClass = null,
  onSelectSpreadClass,
}) => {
  const handleToggle = (id: string) => {
    if (!onSelectSpreadClass) return;
    onSelectSpreadClass(selectedSpreadClass === id ? null : id);
  };

  // Pre-calculate SVG offsets for the doughnut segments
  let cumulativeOffset = 0;
  const segments = SPREAD_CLASSES.map((item) => {
    const fraction = item.count / TOTAL_SPECIES;
    const arcLength = fraction * CIRCUMFERENCE;
    const gap = 3;
    const dashLength = Math.max(2, arcLength - gap);
    const offset = cumulativeOffset;
    cumulativeOffset += arcLength;

    return {
      ...item,
      dashLength,
      offset,
    };
  });

  return (
    <Box
      sx={{
        background: 'linear-gradient(180deg, rgba(18, 24, 21, 0.85) 0%, rgba(10, 14, 12, 0.95) 100%)',
        border: '1px solid #1E2E25',
        borderRadius: '12px',
        p: { xs: 2, md: 2.5 },
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        height: '100%',
        minHeight: '460px',
        boxSizing: 'border-box',
      }}
    >
      {/* Header */}
      <Box sx={{ mb: 2 }}>
        <Typography
          sx={{
            fontSize: '10px',
            color: '#10B981',
            fontWeight: 800,
            letterSpacing: '1px',
            textTransform: 'uppercase',
            mb: 0.5,
          }}
        >
          DISTRIBUTION
        </Typography>
        <Typography
          sx={{
            fontSize: { xs: '16px', md: '18px' },
            fontWeight: 700,
            color: '#FFFFFF',
            lineHeight: 1.2,
          }}
        >
          Species by spread class
        </Typography>
        <Typography sx={{ fontSize: '11px', color: '#888888', mt: 0.5 }}>
          Click a segment to filter the catalogue
        </Typography>
      </Box>

      {/* Main Body: Donut on Left + Legend on Right */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: 'center',
          justifyContent: 'center',
          gap: { xs: 2.5, sm: 3 },
          flex: 1,
        }}
      >
        {/* Doughnut Chart */}
        <Box
          sx={{
            position: 'relative',
            width: { xs: '150px', md: '160px' },
            height: { xs: '150px', md: '160px' },
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <svg
            width="100%"
            height="100%"
            viewBox="0 0 160 160"
            style={{ transform: 'rotate(-90deg)', overflow: 'visible' }}
          >
            {/* Background ring */}
            <circle
              cx={80}
              cy={80}
              r={CIRCLE_RADIUS}
              fill="none"
              stroke="rgba(255, 255, 255, 0.05)"
              strokeWidth={16}
            />

            {/* Segment arcs */}
            {segments.map((seg) => {
              const isSelected = selectedSpreadClass === seg.id;
              const isDimmed = selectedSpreadClass !== null && !isSelected;

              return (
                <circle
                  key={seg.id}
                  cx={80}
                  cy={80}
                  r={CIRCLE_RADIUS}
                  fill="none"
                  stroke={seg.color}
                  strokeWidth={isSelected ? 18 : 15}
                  strokeDasharray={`${seg.dashLength} ${CIRCUMFERENCE - seg.dashLength}`}
                  strokeDashoffset={-seg.offset}
                  style={{
                    cursor: 'pointer',
                    transition: 'stroke-width 0.2s ease, opacity 0.2s ease, filter 0.2s ease',
                    opacity: isDimmed ? 0.3 : 1,
                    filter: isSelected ? `drop-shadow(0 0 6px ${seg.color})` : 'none',
                  }}
                  onClick={() => handleToggle(seg.id)}
                />
              );
            })}
          </svg>

          {/* Center Callout: 75 SPECIES */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              pointerEvents: 'none',
            }}
          >
            <Typography
              sx={{
                fontSize: '26px',
                fontWeight: 800,
                color: '#FFFFFF',
                lineHeight: 1,
              }}
            >
              {TOTAL_SPECIES}
            </Typography>
            <Typography
              sx={{
                fontSize: '9px',
                fontWeight: 700,
                color: '#888888',
                letterSpacing: '0.8px',
                mt: 0.5,
                textTransform: 'uppercase',
              }}
            >
              SPECIES
            </Typography>
          </Box>
        </Box>

        {/* Legend List */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1.25,
            flex: 1,
            width: '100%',
          }}
        >
          {SPREAD_CLASSES.map((item) => {
            const isSelected = selectedSpreadClass === item.id;
            const isDimmed = selectedSpreadClass !== null && !isSelected;

            return (
              <Box
                key={item.id}
                onClick={() => handleToggle(item.id)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  p: '10px 12px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  border: '1px solid',
                  borderColor: isSelected ? `${item.color}88` : 'transparent',
                  bgcolor: isSelected
                    ? 'rgba(255, 255, 255, 0.06)'
                    : 'rgba(255, 255, 255, 0.015)',
                  opacity: isDimmed ? 0.45 : 1,
                  transition: 'all 0.15s ease',
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.04)',
                    borderColor: `${item.color}55`,
                    opacity: 1,
                  },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0, pr: 1 }}>
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      bgcolor: item.color,
                      flexShrink: 0,
                      boxShadow: isSelected ? `0 0 6px ${item.color}` : 'none',
                    }}
                  />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontSize: '12.5px',
                        fontWeight: 600,
                        color: '#FFFFFF',
                        lineHeight: 1.2,
                      }}
                    >
                      {item.name}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '10.5px',
                        color: '#6B7280',
                        mt: 0.25,
                        lineHeight: 1.2,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {item.description}
                    </Typography>
                  </Box>
                </Box>

                {/* Count */}
                <Typography
                  sx={{
                    fontSize: '15px',
                    fontWeight: 700,
                    color: item.color,
                    flexShrink: 0,
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {item.count}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Box>

      {/* Active Filter Helper / Reset Notice */}
      {selectedSpreadClass && (
        <Box
          sx={{
            mt: 2,
            pt: 1.5,
            borderTop: '1px solid #1E2E25',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography sx={{ fontSize: '11px', color: '#10B981' }}>
            Filtering catalogue by{' '}
            <b>{SPREAD_CLASSES.find((s) => s.id === selectedSpreadClass)?.name}</b>
          </Typography>
          <Typography
            onClick={() => onSelectSpreadClass && onSelectSpreadClass(null)}
            sx={{
              fontSize: '11px',
              color: '#888',
              cursor: 'pointer',
              textDecoration: 'underline',
              '&:hover': { color: '#fff' },
            }}
          >
            Clear filter
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default SpeciesSpreadClassCard;
